import React, { type ReactElement, type ReactNode, useRef, useState } from 'react'
import type { DialogRootActions } from '@base-ui/react/dialog'
import {
  Drawer,
  breakpointQueries,
  Button,
  InputGroup,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'

export const LinkModal = ({
  trigger,
  onInsertLink,
}: {
  trigger: ReactElement
  onInsertLink: (url: string) => void
}): ReactNode => {
  const { t } = useTranslation()
  const [url, setUrl] = useState('')
  const actionsRef = useRef<DialogRootActions | null>(null)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })

  const handleSaveLink = (): void => {
    if (url) {
      onInsertLink(url)
    }
    actionsRef.current?.close()
  }
  const closeOnEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSaveLink()
    }
  }

  const title = t('comments.components.comments.add_link_title')

  const fields = (
    <InputGroup.Root className="w-full">
      <InputGroup.Input
        placeholder={t('comments.components.comments.link_menu_placeholder')}
        onKeyDown={closeOnEnterKey}
        value={url}
        onChange={e => setUrl(e.target.value)}
      />
    </InputGroup.Root>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          actionsRef.current?.close()
        }}
        className={buttonClassName}
      >
        {t('comments.components.comments.add_link_cancel')}
      </Button>
      <Button variant="primary" onClick={handleSaveLink} className={buttonClassName}>
        {t('comments.components.comments.add_link_save')}
      </Button>
    </>
  )

  const action = renderAction()

  return isMobile ? (
    <Drawer.Root
      actionsRef={actionsRef}
      onOpenChange={open => {
        if (!open) setUrl('')
      }}
    >
      <Drawer.Trigger render={trigger} />
      <Drawer.Popup>
        <Drawer.Header className="flex-row items-start gap-2.5">
          <Drawer.Title className="min-w-0 flex-1">{title}</Drawer.Title>
          <Drawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </Drawer.Close>
        </Drawer.Header>
        <Drawer.Content className="flex flex-col gap-6">{fields}</Drawer.Content>
        <Drawer.Footer>
          <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
        </Drawer.Footer>
      </Drawer.Popup>
    </Drawer.Root>
  ) : (
    <Modal.Root
      actionsRef={actionsRef}
      onOpenChange={open => {
        if (!open) setUrl('')
      }}
    >
      <Modal.Trigger render={trigger} />
      <Modal.Popup>
        <Modal.Header className="pr-16">
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">{fields}</div>
        <Modal.Footer>{action}</Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}
